import { Component, inject, ChangeDetectionStrategy, input, signal, ElementRef, output, ChangeDetectorRef, computed } from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';
import { IDetailsEpisode } from '@features/details/data-access/details.models';
import { SkinningService } from '@core/skin';
import { AuthStore } from '@core/auth';
import { ContentRatingGate } from '@core/content-ratings';
import { DetailsService } from '@features/details/data-access/details.service';
import { ConverterHelper } from '@shared/utils';
import { IExtrasItemDto } from '@features/assets/data-access/api-shared.dto';
import { BadgeComponent } from 'app/shared/components/badge/badge.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ApiService } from 'app/services/api.service';
import { UxService } from '@core/ux';
import { SettingsStore } from '@core/settings';
import { ContentRatingLock } from '@shared/components/content-rating-lock/content-rating-lock';
import { MyContentStore } from '@features/my-content';
import { BadgeVariant } from 'app/shared/components/badge/badge.model';
import { PlayerService } from 'app/features/player/data-access/player.service';
import { PlayerTypes } from 'app/features/player/data-access/player.config';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { TranscodeImageDirective } from '@shared/directives/transcode-image.directive';
@UntilDestroy()
@Component({
    selector: 'asset-episode-card',
    templateUrl: './asset-episode-card.component.html',
    styleUrls: ['./asset-episode-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    RoundedIconButton,
    BadgeComponent,
    ContentRatingLock,
    TranscodeImageDirective,
    TranslocoPipe
]
})
export class AssetEpisodeCardComponent {
    BadgeVariant = BadgeVariant;

    readonly episodeItem = input<IDetailsEpisode>();
    readonly extrasItem = input<IExtrasItemDto>();
    readonly onPlay = output<any>();
    readonly inPlayer = input<boolean>();
    readonly hasUpcomingEnabled = input<boolean>(false);
    readonly hasOutgoingEnabled = input<boolean>(false);
    readonly showAutomaticBadge = input<boolean>(true);
    readonly availabilityStartPassed = output<boolean>();


    public skinning = inject(SkinningService);
    public service = inject(DetailsService);
    private converter = inject(ConverterHelper);
    public elementRef = inject(ElementRef);
    public api = inject(ApiService);
    public ux = inject(UxService);
    public changeDetector = inject(ChangeDetectorRef);
    private myContentStore = inject(MyContentStore);
    public settings = inject(SettingsStore);
    public auth = inject(AuthStore);
    private gate = inject(ContentRatingGate);
    private playerService = inject(PlayerService);

    public opened = signal(false);
    public isAvailabilityStartPassed = signal<boolean | null>(null);

    constructor() {
    }

    showBadge = computed(() => {
        const item = this.episodeItem();
        if (!item) return false;

        const hasRegularBadge = item.badge !== null && item.badge !== undefined;
        const hasOutgoing = this.hasOutgoingEnabled() && !!item.availabilityEndTime;
        const hasUpcoming = this.hasUpcomingEnabled() && !!item.availabilityStartTime;

        // Automatic "Nova Epizoda" badge: startTime already passed but within the configured window;
        // only the parent-selected (latest available) episode is eligible.
        const now = Date.now();
        const startMs = item.availabilityStartTime ? new Date(item.availabilityStartTime).getTime() : null;
        const autoWindowMs = (this.settings.automaticBadgeDurationInHours() || 48) * 3600 * 1000;
        const hasAutomatic = this.showAutomaticBadge() && !!startMs && startMs <= now && (startMs + autoWindowMs) > now;

        return hasRegularBadge || hasOutgoing || hasUpcoming || hasAutomatic;
    });

    /** Above the active profile's content rating: thumbnail blurred, no play (AVIION-36952). */
    restricted = computed(() => this.gate.isRestricted(this.episodeItem()));

    showPlayButton = computed(() => {
        const item = this.episodeItem();
        if (!item) return false;
        if (this.restricted()) return false;

        if (!item.availabilityStartTime) {
            return true;
        }

        const passedSignal = this.isAvailabilityStartPassed();
        if (passedSignal !== null) {
            return passedSignal;
        }

        // Fallback for initial load before badge emits:
        const startTime = new Date(item.availabilityStartTime).getTime();
        const now = new Date().getTime();

        return startTime <= now;
    });

    onAvailabilityStartPassed(passed: boolean) {
        const previous = this.isAvailabilityStartPassed();
        this.isAvailabilityStartPassed.set(passed);
        if (previous !== passed) {
            this.availabilityStartPassed.emit(passed);
        }
    }

    expandEpisode() {
        this.opened.update(value => !value);
    }

    play() {
        if (this.extrasItem()) {
            this.onPlay.emit(this.extrasItem());
            return;
        }
        this.playerService.changePlayerViewLocation();
        this.playerService.release().subscribe(() => {
            this.playerService.playItem(this.episodeItem() as any, PlayerTypes.Normal);
            // Player now owns the foreground — drop the modal back below it.
            this.service.setOverPlayer(false);
        });
    }

    checkPlural(value: string, singular: string, plural: string): string {
        return this.converter.checkPlural(value, singular, plural);
    }

    checkAnyCredits(): boolean {
        const c = this.episodeItem()?.credits;
        return !!(
            c?.actors || c?.directors || c?.authors || c?.writers ||
            c?.producers || c?.editors || c?.projectEditors ||
            c?.publishers || c?.productionCountries || c?.photoSources
        );
    }

    toggleFavorite(item: IDetailsEpisode, event: MouseEvent) {
        event.stopPropagation();
        if (!this.auth.isAuthenticated()) {
            this.ux.showAnonymousMessage();
            return;
        }
        const referenceId = item?.referenceId;
        if (!referenceId) return;

        const action$ = item.watchLater
            ? this.myContentStore.removeFromWatchLater([referenceId])
            : this.myContentStore.addToWatchLater(referenceId);

        action$.pipe(untilDestroyed(this)).subscribe(success => {
            if (success) {
                item.watchLater = !item.watchLater;
                this.detectChanges();
            } else {
                this.ux.showError();
            }
        });
    }

    public detectChanges() {
        this.changeDetector.detectChanges();
    }
}
